import { TranslationKeys } from '../../types/Translations.type';
import { NO_URL } from '../../constants/Constants';
import { ShowpadLinkHelper } from '../../helpers/ShowpadLinkHelper';
import { interpolateUrlVariables } from '../../helpers/UrlHelper';
import { FullscreenHelper } from '../..';
import { browser } from '../../helpers/BrowserHelper';
import { FullscreenActionBar } from '../FullscreenActionBar';
import { ViewerEventType, ViewerWebappCloseEvent, ViewerWebappOpenEvent } from '../../types/ViewerEvents.type';
export class UrlIframePresentationRenderer {
    constructor(assetViewerView, presentation) {
        this.assetViewerView = assetViewerView;
        this.presentation = presentation;
        this.hasMetaData = true;
        this.isLoading = false;
        this.isActive = false;
        this.height = null;
        this.container = null;
        this.containerContent = null;
        this.isFullscreen = false;
    }
    get config() {
        return this.assetViewerView.config;
    }
    get translations() {
        return this.config.TRANSLATIONS;
    }
    get url() {
        const url = this.checkUrl(this.presentation);
        if (!url || (url && url.toLowerCase().indexOf('javascript') === 0)) {
            return NO_URL;
        }
        else if (!/^(.*:\/\/)/.test(url) && url.toLowerCase().indexOf('mailto:') !== 0) {
            return `http://${interpolateUrlVariables(url, this.config)}`;
        }
        else {
            return interpolateUrlVariables(url, this.config);
        }
    }
    get previewUrl() {
        return this.assetViewerView.assetViewerItem.previewUrl;
    }
    get name() {
        const splitUrl = this.assetViewerView.assetViewerItem.displayName.split('.url');
        return splitUrl.length === 2 && splitUrl[1] === '' ? splitUrl[0] : this.assetViewerView.assetViewerItem.displayName;
    }
    get dimensions() {
        if (!this.height) {
            return null;
        }
        const { config } = this.assetViewerView;
        const extraSpace = config.VIEW_PADDING_TOP + config.VIEW_PADDING_BOTTOM + 2 * config.VIEW_BORDER;
        return {
            width: Math.min(config.MAX_CONTENT_WIDTH, this.assetViewerView.viewerLayout.viewportWidth - extraSpace),
            height: this.height
        };
    }
    checkUrl(presentation) {
        if (!!presentation.url) {
            return presentation.url;
        }
        else if (!!presentation.urls) {
            return presentation.urls[0];
        }
        else {
            return null;
        }
    }
    load() {
        return Promise.resolve();
    }
    renderActionBar(container, iframe) {
        this.actionBar = new FullscreenActionBar(this.assetViewerView.config, this.assetViewerView.eventBus, this.assetViewerView);
        this.actionBar.render(container, iframe);
    }
    renderIframe() {
        this.isLoading = true;
        // Workaround for mobile safari scroll+overflow issue with iframes
        const wrapper = document.createElement('div');
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        wrapper.style.webkitOverflowScrolling = 'touch';
        wrapper.style.background = 'black';
        // iframe scroll hack for mobile safari
        if (browser.ios || (browser.safari && 'ontouchstart' in window)) {
            wrapper.style.overflow = 'auto';
        }
        wrapper.classList.add('iframe-wrapper');
        const iframe = (this.iframe = document.createElement('iframe'));
        iframe.classList.add('av-webapp-iframe');
        wrapper.appendChild(iframe);
        iframe.scrolling = 'yes';
        iframe.onload = onLoad.bind(this);
        iframe.onerror = onError.bind(this);
        iframe.src = this.url;
        this.assetViewerView.config.container.parentNode.appendChild(wrapper);
        this.newWindow = wrapper;
        this.iframe.src = this.url;
        this.renderActionBar(wrapper, iframe);
        if (!FullscreenHelper.isFullscreen()) {
            FullscreenHelper.enterFullscreen(wrapper);
        }
        this.isFullscreen = true;
        this.assetViewerView.eventBus.on(ViewerEventType.FullscreenChange, () => {
            if (this.isFullscreen === true && !FullscreenHelper.isFullscreen()) {
                wrapper.style.display = 'none';
                this.isFullscreen = null;
                this.assetViewerView.eventBus.dispatch(new ViewerWebappCloseEvent({ assetViewerItem: this.assetViewerView.assetViewerItem }));
            }
        });
        function onLoad() {
            this.isLoading = false;
            this.width = 400;
            this.height = 300;
            iframe.onload = null;
            iframe.onerror = null;
        }
        function onError(error) {
            this.isLoading = false;
            iframe.onload = null;
            iframe.onerror = null;
            this.iframe = null;
        }
    }
    render(container) {
        this.container = container;
        return Promise.resolve().then(() => {
            const urlWrapper = document.createElement('div');
            this.containerContent = urlWrapper;
            urlWrapper.classList.add('renderer-url');
            const link = document.createElement('a');
            link.classList.add('link');
            if (this.url !== NO_URL) {
                link.href = this.url;
                if (ShowpadLinkHelper.isShowpadLink(this.url)) {
                    link.onclick = () => {
                        // prevent postMessage being fired twice when showpad-js-web is loaded on the page
                        if (!(typeof window !== 'undefined' && window.ShowpadLib)) {
                            ShowpadLinkHelper.handleShowpadAssetLink({
                                target: link.target,
                                uri: link.href
                            });
                        }
                    };
                }
                else {
                    link.onclick = event => {
                        event.preventDefault();
                        if (this.newWindow) {
                            this.newWindow.style.display = 'block';
                            FullscreenHelper.enterFullscreen(this.newWindow);
                            this.isFullscreen = true;
                        }
                        else {
                            this.renderIframe();
                        }
                        this.assetViewerView.eventBus.dispatch(new ViewerWebappOpenEvent({ assetViewerItem: this.assetViewerView.assetViewerItem }));
                    };
                }
            }
            const urlInfoWrapper = document.createElement('div');
            urlInfoWrapper.classList.add('info-wrapper');
            const urlInfo = document.createElement('div');
            urlInfo.classList.add('info');
            const urlName = document.createElement('div');
            urlName.classList.add('name');
            urlName.innerText = this.name;
            urlInfo.appendChild(urlName);
            const linkPreviewWrapper = document.createElement('div');
            linkPreviewWrapper.classList.add('link-preview-wrapper');
            const previewImageWrapper = document.createElement('div');
            previewImageWrapper.classList.add('link-preview');
            previewImageWrapper.innerHTML = `<img src="${this.previewUrl}" alt="${this.translations[TranslationKeys.PreviewImage]}"/>`;
            if (this.assetViewerView.assetViewerItem.assetDescription) {
                const linkDescription = document.createElement('div');
                linkDescription.classList.add('link-description');
                linkDescription.innerText = this.assetViewerView.assetViewerItem.assetDescription;
                urlInfo.appendChild(linkDescription);
            }
            link.innerHTML = ` ${this.translations[TranslationKeys.VisitUrl]}`;
            urlInfo.appendChild(link);
            container.appendChild(urlWrapper);
            linkPreviewWrapper.appendChild(previewImageWrapper);
            urlInfoWrapper.appendChild(linkPreviewWrapper);
            urlInfoWrapper.appendChild(urlInfo);
            urlWrapper.appendChild(urlInfoWrapper);
            this.container.appendChild(urlWrapper);
            // calculate height based on the DOM element after it has been added to the DOM
            this.height = this.containerContent.offsetHeight;
        });
    }
    resize() {
        if (this.height && this.containerContent) {
            this.height = this.containerContent.offsetHeight;
        }
    }
    destroy() {
        this.iframe = null;
        if (this.container) {
            this.container.innerHTML = '';
            this.container = null;
        }
    }
    destroyFinal() {
        this.destroy();
    }
}
//# sourceMappingURL=UrlIframePresentationRenderer.js.map